import { createBrowserRouter, redirect, type LoaderFunctionArgs } from 'react-router';
import BootFallback from './components/BootFallback';
import RootLayout, { rootLoader } from './routes/rootLayout';
import RouteError from './routes/routeError';
import SignIn, { signInAction, signInLoader } from './routes/signIn';
import SignUp, { signUpAction, signUpLoader } from './routes/signUp';
import AuthCallback, { authCallbackLoader } from './routes/authCallback';
import SignOut, { signOutLoader } from './routes/signOut';
import Welcome, { welcomeAction, welcomeLoader } from './routes/welcome';
import Invitation, { InvitationError, invitationAction, invitationLoader } from './routes/invitation';
import Home, { homeAction, homeLoader } from './routes/home';
import Review, { reviewLoader } from './routes/review';
import Studios, { studiosAction, studiosLoader } from './routes/studios';

const hydrateFallbackElement = <BootFallback />;

export const router = createBrowserRouter([
  {
    path: '/signin',
    element: <SignIn />,
    loader: signInLoader,
    action: signInAction,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/signup',
    element: <SignUp />,
    loader: signUpLoader,
    action: signUpAction,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/welcome',
    element: <Welcome />,
    loader: welcomeLoader,
    action: welcomeAction,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/signout',
    element: <SignOut />,
    loader: signOutLoader,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/auth/callback',
    element: <AuthCallback />,
    loader: authCallbackLoader,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/invite',
    element: <Invitation />,
    loader: invitationLoader,
    action: invitationAction,
    hydrateFallbackElement,
    errorElement: <InvitationError />
  },
  {
    path: '/studios',
    element: <Studios />,
    loader: studiosLoader,
    action: studiosAction,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/review',
    element: <Review />,
    loader: reviewLoader,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    path: '/review.html',
    element: <Review />,
    loader: reviewLoader,
    hydrateFallbackElement,
    errorElement: <RouteError />
  },
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    loader: rootLoader,
    hydrateFallbackElement,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Home />, loader: homeLoader, action: homeAction, hydrateFallbackElement },
      { path: 'generator', lazy: async () => { const route = await import('./routes/generator'); return { Component: route.default, loader: route.generatorLoader }; }, hydrateFallbackElement },
      { path: 'viral', lazy: async () => { const route = await import('./routes/viral'); return { Component: route.default, loader: route.viralLoader }; }, hydrateFallbackElement },
      { path: 'viral/admin', lazy: async () => { const route = await import('./routes/viralAdmin'); return { Component: route.default, loader: route.viralAdminLoader }; }, hydrateFallbackElement },
      { path: 'guild', lazy: async () => { const route = await import('./routes/guild'); return { Component: route.default, loader: route.guildLoader }; }, hydrateFallbackElement },
      { path: 'guild/jobs/new', lazy: async () => { const route = await import('./routes/guildJobNew'); return { Component: route.default, loader: route.guildJobNewLoader }; }, hydrateFallbackElement },
      { path: 'guild/jobs/:jobId', lazy: async () => { const route = await import('./routes/guildJob'); return { Component: route.default, loader: route.guildJobLoader }; }, hydrateFallbackElement },
      { path: 'guild/contracts/:contractId', lazy: async () => { const route = await import('./routes/guildContract'); return { Component: route.default, loader: route.guildContractLoader }; }, hydrateFallbackElement },
      { path: 'guild/artists/:handle', lazy: async () => { const route = await import('./routes/guildArtist'); return { Component: route.default, loader: route.guildArtistLoader }; }, hydrateFallbackElement },
      { path: 'guild/profile', lazy: async () => { const route = await import('./routes/guildProfile'); return { Component: route.default, loader: route.guildProfileLoader }; }, hydrateFallbackElement },
      { path: 'cost', lazy: async () => { const route = await import('./routes/cost'); return { Component: route.default, loader: route.costLoader }; }, hydrateFallbackElement },
      { path: 'pricing', lazy: async () => { const route = await import('./routes/pricing'); return { Component: route.default, loader: route.pricingLoader, action: route.pricingAction }; }, hydrateFallbackElement },
      { path: 'reviews', lazy: async () => { const route = await import('./routes/reviews'); return { Component: route.default, loader: route.reviewsLoader, shouldRevalidate: route.reviewsShouldRevalidate }; }, hydrateFallbackElement },
      { path: 'scheduler', lazy: async () => { const route = await import('./routes/scheduler'); return { Component: route.default, loader: route.schedulerLoader, shouldRevalidate: route.schedulerShouldRevalidate }; }, hydrateFallbackElement },
      { path: 'settings', lazy: async () => { const route = await import('./routes/settings'); return { Component: route.default, loader: route.settingsLoader, shouldRevalidate: route.settingsShouldRevalidate }; }, hydrateFallbackElement },
      {
        path: 'projects/:projectId',
        loader: ({ params }: LoaderFunctionArgs) => redirect(`/projects/${params.projectId}/idea`),
        hydrateFallbackElement
      },
      { path: 'series/:seriesId', lazy: async () => { const route = await import('./routes/series'); return { Component: route.default, loader: route.seriesLoader }; }, hydrateFallbackElement },
      { path: 'projects/:projectId/saas', lazy: async () => { const route = await import('./routes/saasStudio'); return { Component: route.default, loader: route.saasStudioLoader }; }, hydrateFallbackElement },
      { path: 'projects/:projectId/raw', lazy: async () => { const route = await import('./routes/rawStudio'); return { Component: route.default, loader: route.rawStudioLoader }; }, hydrateFallbackElement },
      { path: 'projects/:projectId/jobs', lazy: async () => { const route = await import('./routes/projectJobs'); return { Component: route.default, loader: route.projectJobsLoader }; }, hydrateFallbackElement },
      { path: 'projects/:projectId/agents', lazy: async () => { const route = await import('./routes/projectAgents'); return { Component: route.default, loader: route.projectAgentsLoader }; }, hydrateFallbackElement },
      { path: 'projects/:projectId/canvas/:boardId', lazy: async () => { const route = await import('./routes/canvas'); return { Component: route.default, loader: route.canvasLoader }; }, hydrateFallbackElement },
      { id: 'studio', path: 'projects/:projectId/:stageId', lazy: async () => { const route = await import('./routes/studio'); return { Component: route.default, loader: route.studioLoader }; }, hydrateFallbackElement }
    ]
  }
]);
